import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import VideoCard from "@/components/VideoCard";
import NewsletterSignup from "@/components/NewsletterSignup";
import SEO from "@/components/SEO";
import AdSlot from "@/components/AdSlot";
import { CATEGORIES } from "@/lib/categories";
import { Link } from "react-router-dom";
import HeroMap from "@/components/HeroMap";
import SocialLinks from "@/components/SocialLinks";
import { ArrowUpRight, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";

export default function Home() {
  const [speeches, setSpeeches] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/speeches").then((r) => setSpeeches(r.data)).finally(() => setLoading(false));
  }, []);

  const themes = CATEGORIES;
  const nLeaders = new Set(speeches.map((s) => s.leader).filter(Boolean)).size;
  const nCountries = new Set(speeches.map((s) => s.country).filter(Boolean)).size;
  const tickerItems = speeches.slice(0, 10);

  const jsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "WebSite",
      name: "Smart News Italia",
      url: "https://smartnewsitalia.it/",
      inLanguage: "it-IT",
      potentialAction: {
        "@type": "SearchAction",
        target: "https://smartnewsitalia.it/archivio?search={search_term_string}",
        "query-input": "required name=search_term_string",
      },
    },
    {
      "@context": "https://schema.org",
      "@type": "Organization",
      name: "Smart News Italia",
      url: "https://smartnewsitalia.it/",
      logo: "https://smartnewsitalia.it/favicon-512.png",
      sameAs: [
        "https://www.youtube.com/@SmartNewsIT",
        "https://www.instagram.com/smart.newsit/",
        "https://www.tiktok.com/@smartnewsita",
        "https://www.facebook.com/profile.php?id=61574651338924",
      ],
    },
  ];

  return (
    <div>
      <SEO canonicalPath="/" jsonLd={jsonLd} />
      {/* ===== HERO ===== */}
      <section className="relative overflow-hidden bg-[#070b18] border-b border-border">
        {/* Mappa mondiale decorativa di sfondo (resta sempre presente) */}
        <img src="/hero-bg.jpg" alt="" aria-hidden="true" className="absolute inset-0 w-full h-full object-cover opacity-50" />
        {/* Sfumatura: più scura a sinistra (dietro il testo), più chiara a destra */}
        <div className="absolute inset-0 bg-gradient-to-r from-[#070b18] via-[#070b18]/90 to-[#070b18]/45" />
        {/* Sfuma verso la sezione successiva */}
        <div className="absolute inset-0 bg-gradient-to-t from-[#070b18] via-transparent to-transparent" />
        <div className="hero-glow hero-glow-1" />
        <div className="hero-glow hero-glow-2" />

        <div className="relative z-10 max-w-7xl mx-auto px-5 sm:px-8 pt-16 lg:pt-24 pb-14">
          <div className="grid lg:grid-cols-12 gap-10 items-stretch">
            <div className="lg:col-span-5 flex flex-col">
              <div className="flex items-center gap-2 text-xs uppercase tracking-[0.22em] font-semibold text-[#8ba0ff] mb-5">
                <span className="live-dot inline-block h-2 w-2 rounded-full bg-[#4f6bff]" />
                Discorsi, dichiarazioni e conferenze stampa · Testo originale · Traduzione italiana
              </div>
              <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.05] text-white">
                <span className="block">Le parole che</span>{" "}
                <span className="block text-[#6d86ff]">fanno la storia.</span>
              </h1>
              <p className="text-base sm:text-lg text-white/75 mt-6 max-w-lg leading-relaxed">
                Discorsi, dichiarazioni e conferenze stampa dal mondo, tradotti in italiano
                con video originale, testo integrale e fonte ufficiale.
              </p>
              <div className="flex flex-wrap gap-3 mt-8">
                <Button asChild className="rounded-sm h-11 px-6 gap-1.5" data-testid="hero-archive-btn">
                  <Link to="/archivio">Esplora l'archivio <ArrowUpRight className="h-4 w-4" /></Link>
                </Button>
                <Button asChild variant="outline" className="rounded-sm h-11 px-6 bg-white/5 text-white border-white/25 hover:bg-white/15 hover:text-white" data-testid="hero-method-btn">
                  <Link to="/metodologia">Scopri il nostro metodo</Link>
                </Button>
              </div>
              <div className="flex items-center gap-8 mt-10 lg:mt-auto" data-testid="hero-counters">
                {[["Discorsi", speeches.length], ["Leader", nLeaders], ["Paesi", nCountries]].map(([label, val]) => (
                  <div key={label}>
                    {loading ? (
                      <Skeleton className="h-9 w-12 bg-white/15" />
                    ) : (
                      <div className="font-serif text-3xl font-bold text-white" data-testid={`counter-${label.toLowerCase()}`}>{val}</div>
                    )}
                    <div className="text-[11px] uppercase tracking-[0.2em] text-white/60 mt-1">{label}</div>
                  </div>
                ))}
              </div>
            </div>

            <div className="lg:col-span-7 flex flex-col">
              <div className="flex-1 flex items-center">
                <div className="w-full">
                  <HeroMap speeches={speeches} />
                </div>
              </div>
              <div className="flex items-center gap-3 mt-6 justify-center lg:justify-end">
                <span className="text-[11px] uppercase tracking-[0.2em] font-semibold text-white/60">Seguici</span>
                <SocialLinks variant="onDark" />
              </div>
            </div>
          </div>
        </div>

        {/* News ticker */}
        {tickerItems.length > 0 && (
          <div className="relative z-10 border-t border-white/10 bg-black/40 backdrop-blur" data-testid="news-ticker">
            <div className="flex items-stretch">
              <div className="shrink-0 flex items-center gap-2 px-4 sm:px-5 border-r border-white/10 text-[11px] font-bold uppercase tracking-[0.18em] text-[#8ba0ff]">
                <span className="live-dot inline-block h-2 w-2 rounded-full bg-[#4f6bff]" />
                Appena pubblicati
              </div>
              <div className="ticker-mask overflow-hidden flex-1 py-3">
                <div className="animate-marquee">
                  {[...tickerItems, ...tickerItems].map((s, i) => (
                    <Link
                      key={`${s.id}-${i}`}
                      to={`/discorso/${s.id}`}
                      className="text-sm text-white/70 hover:text-white transition-colors mx-6 inline-flex items-center gap-2"
                    >
                      <span className="h-1 w-1 rounded-full bg-[#4f6bff]" />
                      <span className="font-medium text-white/50">{s.leader}:</span> {s.title}
                    </Link>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}
      </section>

      <div className="max-w-7xl mx-auto px-5 sm:px-8 py-12">
        <AdSlot placement="home_top" className="mb-12" />

        {/* Latest grid */}
        <section className="mb-16">
          <div className="flex items-baseline justify-between border-b border-border pb-4 mb-8">
            <h2 className="font-serif text-2xl sm:text-3xl font-bold">Ultimi discorsi pubblicati</h2>
            <Link to="/archivio" className="text-sm text-primary hover:underline">Vai all'archivio</Link>
          </div>
          {loading ? (
            <div className="py-16 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
          ) : speeches.length === 0 ? (
            <p className="text-muted-foreground">Nessun discorso pubblicato al momento.</p>
          ) : (
            <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
              {speeches.map((s) => (
                <VideoCard key={s.id} speech={s} />
              ))}
            </div>
          )}
        </section>

        {/* Themes */}
        {themes.length > 0 && (
          <section className="mb-16">
            <h2 className="text-xs uppercase tracking-[0.2em] font-semibold text-muted-foreground mb-4">
              Esplora per argomento
            </h2>
            <div className="flex flex-wrap gap-3">
              {themes.map((t) => (
                <Button key={t} asChild variant="outline" className="rounded-sm" data-testid={`theme-chip-${t}`}>
                  <Link to={`/archivio?theme=${encodeURIComponent(t)}`}>{t}</Link>
                </Button>
              ))}
            </div>
          </section>
        )}

        {/* Social */}
        <section className="mb-16" data-testid="home-social">
          <div className="bg-card border border-border rounded-sm px-6 sm:px-10 py-10 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-6">
            <div>
              <h2 className="text-xs uppercase tracking-[0.2em] font-semibold text-muted-foreground mb-2">
                Seguici sui social
              </h2>
              <p className="font-serif text-2xl sm:text-3xl font-bold leading-tight">
                I discorsi che contano, anche su YouTube, Instagram, TikTok e Facebook
              </p>
            </div>
            <SocialLinks size="lg" iconClassName="h-5 w-5" className="shrink-0" />
          </div>
        </section>

        <NewsletterSignup />
      </div>
    </div>
  );
}
